import React, { useState, useEffect } from 'react';
import BrainLoader from './BrainLoader';
import { useLanguage } from './LanguageContext';
import PortfolioReport from './PortfolioReport';
import PortfolioRevenue from './PortfolioRevenue';
import PortfolioCalculator from './PortfolioCalculator';
import { compactAED } from './portfolioMetrics';
import UnitValuationReport from './UnitValuationReport';
import { AirbnbReportSection } from '../portals/owner/AirbnbIncome';
import { appLanguage, trData } from '../i18n/locale';
import {
  FileText,
  ChevronRight,
  Building2,
  Calculator,
  Home,
  RefreshCw,
  Shield
} from 'lucide-react';

// ============================================
// TYPES
// ============================================
interface Property {
  id: string;
  name?: string;
  project?: string;
  location?: string;
  unitNumber?: string;
  area?: string | number;
  areaUnit?: 'sqm' | 'sqft';
  bedrooms?: number | string;
  floor?: number | string;
  type?: string;
  purchaseValue?: number;
  currentValue?: number;
  monthlyRental?: number;
  purchaseDate?: string;
  otpDate?: string;
  createdAt?: string;
  occupancyStatus?: string;
  developer?: string;
  appreciationAmount?: number;
  appreciationPercentage?: number;
}

interface PortfolioViewProps {
  properties: Property[];
  investorId?: string;
  apiUrl?: string;
  investorName?: string;
  /**
   * Preloaded DLD market-intelligence payload, same shape as the
   * /dld/market-intelligence response. Set ONLY by the login-free product
   * tour (portals/demo), which has no investor and no token to fetch with.
   * When present the fetch below is skipped entirely, so the tour renders
   * these real screens without touching the backend. Absent in the app.
   */
  dldDataOverride?: any;
  /** Sample leases for the showcase — see PortfolioReport.leasesOverride. */
  leasesOverride?: any;
  /** Sample payment plans for the showcase — see PortfolioReport.plansOverride. */
  plansOverride?: any;
  /**
   * Sample benchmark payloads keyed by unit id, for the showcase. Real sessions
   * leave this undefined and the benchmark is fetched per selected unit.
   */
  benchmarkOverride?: Record<string, any>;
  /** Sample project timelines keyed by unit id, for the showcase. */
  timelineOverride?: Record<string, any>;
  /** Sample Dubai Sell inventory for the showcase — see PortfolioCalculator. */
  listingsOverride?: any[];
  /** Which tab opens first. Default 'report', as before. */
  initialTab?: 'report' | 'valuation' | 'calculator';
  /**
   * Which tabs to offer. Default is all three, which is what the product tour
   * (portals/demo/AssetDeskDemo) films.
   *
   * The owner shell passes ['report', 'valuation']: this is no longer a
   * bottom-bar destination there but a report opened from the property list,
   * and the Calculator is a tool rather than a view of the portfolio, so it
   * moved to More with the other calculators.
   */
  tabsShown?: Array<'report' | 'valuation' | 'calculator'>;
}

// ============================================
// CORPORATE DESIGN CONSTANTS
// ============================================
const COLORS = {
  gold: '#D6B160',
  black: '#000000',
  white: '#FFFFFF',
  darkBg: '#1a1a2e',
  lightGray: '#f8f9fa',
  mediumGray: '#e5e5e5',
  textGray: '#666666',
  textDark: '#1a1a2e',
  success: '#22c55e',
  danger: '#ef4444'
};

const SQM_TO_SQFT = 10.764;
const SQFT_TO_SQM = 0.0929;

// ============================================
// UTILITY FUNCTIONS
// ============================================
const isLikelySqm = (area: number): boolean => {
  return area > 0 && area < 300;
};

const formatAreaDisplay = (area: string | number | undefined): { 
  primary: string; 
  secondary: string;
  sqft: number;
  sqm: number;
  bothDisplay: string;
} => {
  const areaNum = typeof area === 'string' ? parseFloat(area) || 0 : (area || 0);
  
  if (areaNum === 0) return { primary: '-', secondary: '', sqft: 0, sqm: 0, bothDisplay: '-' };
  
  if (isLikelySqm(areaNum)) {
    const sqft = Math.round(areaNum * SQM_TO_SQFT);
    return {
      primary: `${areaNum.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} m²`,
      secondary: `${sqft.toLocaleString('en-AE')} sqft`,
      sqft: sqft,
      sqm: areaNum,
      bothDisplay: `${areaNum.toFixed(2)} m² / ${sqft.toLocaleString()} sqft`
    };
  } else {
    const sqm = areaNum * SQFT_TO_SQM;
    return {
      primary: `${Math.round(areaNum).toLocaleString('en-AE')} sqft`,
      secondary: `${sqm.toFixed(1)} m²`,
      sqft: areaNum,
      sqm: sqm,
      bothDisplay: `${sqm.toFixed(2)} m² / ${Math.round(areaNum).toLocaleString()} sqft`
    };
  }
};

// ============================================
// MARKET-INTELLIGENCE CACHE
//
// InvestorPortal mounts this component as `activeTab === 'portfolio' && <.../>`,
// so leaving the Portfolio tab unmounts it and coming back remounts it — and
// refetched the whole market-intelligence payload every time, leaving the
// report showing purchase prices instead of valuations until it landed.
//
// One entry per investor, shared across mounts, with the in-flight promise
// stored so two mounts in quick succession make one request. The TTL is short
// enough that a long session still picks up the daily DLD refresh, and the
// payload carries its own lastSync for the freshness note.
// ============================================
const DLD_CACHE_TTL_MS = 3 * 60 * 1000; // 3 min (was 5): corrected values reach the screen sooner
const dldCache = new Map<string, { at: number; data: any }>();
/** The cached payload, if the Portfolio tab has already fetched it — never
 *  starts a request (see the Home screen's use of it). */
export function cachedMarketIntelligence(investorId: string): any | null {
  return dldCache.get(investorId)?.data || null;
}

// The Home value counter needs only each unit's DLD value and confidence, so a
// slim copy of the last payload is kept on the device: the counter then shows
// on app open without anyone waiting for /market-intelligence, which on a
// freshly started backend takes 40–120s while the DLD history loads.
const SLIM_KEY = (investorId: string) => `dldSlim_${investorId}`;
function storeSlim(investorId: string, data: any) {
  try {
    const slim = {
      savedAt: Date.now(),
      lastSync: data?.lastSync || null,
      units: (data?.units || []).map((u: any) => ({
        unitId: u?.unitId, dldMarketValue: u?.dldMarketValue, confidence: u?.confidence,
        dldAvgAnnualRent: u?.dldAvgAnnualRent,
        // Home labels a premium-carried value as such (CopilotBriefing).
        valuationBasis: u?.valuationBasis,
      })),
    };
    localStorage.setItem(SLIM_KEY(investorId), JSON.stringify(slim));
  } catch { /* storage full or blocked — Home just waits for the fetch */ }
}
// Copies saved before the valuation rules last changed describe the old rules
// and are ignored, so Home fetches the new figures instead of showing them
// forever (a device copy is otherwise only replaced from the Portfolio tab).
// 2026-11-04: offices on office sales, DLD name spellings, villas on plot;
// later that day Dada's units valued with the premium his prices paid
// (leaseValuation.js, live 01110-bl6 ~15:40Z).
const VALUATION_RULES_SINCE = Date.parse('2026-11-04T19:04:00Z');
const isCurrentCopy = (saved: any) => Number(saved?.savedAt) >= VALUATION_RULES_SINCE;

/** The slim copy saved by the last successful fetch on this device, if any. */
export function storedMarketIntelligence(investorId: string): { savedAt: number; lastSync: string | null; units: any[] } | null {
  try {
    const raw = localStorage.getItem(SLIM_KEY(investorId));
    const slim = raw ? JSON.parse(raw) : null;
    return slim && isCurrentCopy(slim) && Array.isArray(slim.units) && slim.units.length ? slim : null;
  } catch { return null; }
}
// The Valuation list and the report need the whole payload, not the slim copy,
// so the last one is also kept on the device. Without it every app open (and
// every cold backend) left the list on grey placeholders for as long as the
// fetch took; now the last known valuations show at once and are swapped for
// the fresh ones when they land.
const FULL_KEY = (investorId: string) => `dldFull_${investorId}`;
function storeFull(investorId: string, data: any) {
  try {
    localStorage.setItem(FULL_KEY(investorId), JSON.stringify({ savedAt: Date.now(), data }));
  } catch { /* quota exceeded or blocked — the list just waits for the fetch */ }
}
export function storedFullMarketIntelligence(investorId: string): any | null {
  try {
    const raw = localStorage.getItem(FULL_KEY(investorId));
    const saved = raw ? JSON.parse(raw) : null;
    return saved?.data && isCurrentCopy(saved) && Array.isArray(saved.data.units) && saved.data.units.length ? saved.data : null;
  } catch { return null; }
}
const dldInFlight = new Map<string, Promise<any>>();

export function fetchMarketIntelligence(apiUrl: string, investorId: string, token: string): Promise<any> {
  const key = investorId;
  const cached = dldCache.get(key);
  if (cached && Date.now() - cached.at < DLD_CACHE_TTL_MS) return Promise.resolve(cached.data);

  const inFlight = dldInFlight.get(key);
  if (inFlight) return inFlight;

  const request = fetch(`${apiUrl}/dld/market-intelligence?investorId=${investorId}`, {
    headers: { Authorization: `Bearer ${token}` }
  })
    .then(r => (r.ok ? r.json() : null))
    .then(payload => {
      const data = payload?.success ? payload.data : null;
      // Only a real payload is cached; a failure must not pin "no market data"
      // for the next five minutes.
      if (data) { dldCache.set(key, { at: Date.now(), data }); storeSlim(key, data); storeFull(key, data); }
      return data;
    })
    .finally(() => { dldInFlight.delete(key); });

  dldInFlight.set(key, request);
  return request;
}

// ============================================
// MAIN COMPONENT
// ============================================
const PortfolioView: React.FC<PortfolioViewProps> = ({ properties, investorId, apiUrl, investorName, dldDataOverride, leasesOverride, plansOverride, benchmarkOverride, timelineOverride, listingsOverride, initialTab, tabsShown }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'report' | 'valuation' | 'calculator'>(initialTab || 'report');
  // Seed from the cache so a remount renders valuations on the first paint
  // instead of purchase-price fallbacks that jump a second later.
  // Fall back to the device copy from the last visit, so the list shows
  // valuations immediately rather than waiting on the network.
  const cachedDld = investorId
    ? (dldCache.get(investorId)?.data || storedFullMarketIntelligence(investorId))
    : null;
  const [dldData, setDldData] = useState<any>(dldDataOverride || cachedDld || null);
  // A fresh payload is on its way while rows already show the device copy.
  // A unit missing from that copy (added since) keeps its placeholder until
  // the fetch lands, rather than briefly showing purchase price.
  const [dldRefreshing, setDldRefreshing] = useState<boolean>(
    !dldDataOverride && !!investorId && !!apiUrl && !(investorId && dldCache.get(investorId))
  );
  // True only while the FIRST payload is still in flight, i.e. we have no
  // valuations to show yet. Computed here rather than set from the effect
  // below, because an effect runs after the first paint — which would flash the
  // purchase-price fallback for a frame before the placeholder appeared.
  const [dldLoading, setDldLoading] = useState<boolean>(
    !dldDataOverride && !cachedDld && !!investorId && !!apiUrl
  );
  const [syncing, setSyncing] = useState(false);
  // Valuation tab: when set, the full valuation report for that unit replaces the list.
  const [selectedValuationUnit, setSelectedValuationUnit] = useState<string | null>(null);
  // Benchmark for the selected unit only — fetched on demand rather than for the
  // whole portfolio, because it is a per-unit comparison and most units are
  // never opened.
  const [benchmark, setBenchmark] = useState<any>(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  // Construction milestones + escrow account. Owned data that was displayed
  // nowhere; it matters most for off-plan units still being built.
  const [timeline, setTimeline] = useState<any>(null);

  const units = properties || [];

  // Fetch DLD market intelligence
  useEffect(() => {
    // Product tour supplies the payload up front and has no session to fetch
    // with — bail before any network call.
    if (dldDataOverride) return;
    // Nothing to wait for in these cases, so stop showing the placeholder and
    // let the rows fall back to purchase price.
    if (!investorId || !apiUrl) { setDldLoading(false); setDldRefreshing(false); return; }
    const token = localStorage.getItem('authToken');
    if (!token) { setDldLoading(false); setDldRefreshing(false); return; }
    let alive = true;
    fetchMarketIntelligence(apiUrl, investorId, token)
      .then(data => { if (alive && data) setDldData(data); })
      .catch(() => { /* market data unavailable — the device copy, if any, stays */ })
      .finally(() => { if (alive) { setDldLoading(false); setDldRefreshing(false); } });
    return () => { alive = false; };
  }, [investorId, apiUrl, dldDataOverride]);

  useEffect(() => {
    if (!selectedValuationUnit) { setBenchmark(null); setTimeline(null); return; }

    // Showcase: sample payloads, no network.
    if (benchmarkOverride) {
      setBenchmark(benchmarkOverride[selectedValuationUnit] || null);
      setTimeline(timelineOverride ? (timelineOverride[selectedValuationUnit] || null) : null);
      return;
    }
    if (!apiUrl) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    let alive = true;
    setBenchmarkLoading(true);
    fetch(`${apiUrl}/dld/benchmark/${encodeURIComponent(selectedValuationUnit)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d?.success) setBenchmark(d.data); })
      .catch(() => { /* comparison is additive — the report stands without it */ })
      .finally(() => { if (alive) setBenchmarkLoading(false); });

    fetch(`${apiUrl}/dld/project-timeline/${encodeURIComponent(selectedValuationUnit)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d?.success) setTimeline(d.data); })
      .catch(() => { /* additive — the report stands without it */ });

    return () => { alive = false; };
  }, [selectedValuationUnit, apiUrl, benchmarkOverride, timelineOverride]);

  const handleSyncDLD = async () => {
    if (!apiUrl) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${apiUrl}/dld/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ investorId })
      });
      // Refetch
      const res = await fetch(`${apiUrl}/dld/market-intelligence?investorId=${investorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.success) {
          setDldData(data.data);
          // A sync deliberately fetches fresh data, so replace the cached entry
          // rather than leaving the next mount to read the pre-sync payload.
          if (investorId) { dldCache.set(investorId, { at: Date.now(), data: data.data }); storeFull(investorId, data.data); }
        }
      }
    } catch { /* ignore */ }
    setSyncing(false);
  };

  // Helper to get DLD data for a specific unit
  const getDldUnit = (unitId: string, projectName?: string) => {
    // First try matching by unit ID
    const byId = dldData?.units?.find((u: any) => u.unitId === unitId);
    if (byId && byId.comparablesCount > 0) return byId;
    // Fallback: match by project name (unit IDs may differ between Firestore and memory)
    if (projectName) {
      const byProject = dldData?.units?.find((u: any) => {
        const up = (u.project || '').toLowerCase().trim();
        const pp = projectName.toLowerCase().trim();
        return up === pp || up.includes(pp) || pp.includes(up);
      });
      if (byProject && byProject.comparablesCount > 0) return byProject;
    }
    return byId || null;
  };

  // Tab configuration
  const tabs = [
    { id: 'report' as const, label: t('pfvReport'), icon: FileText },
    { id: 'valuation' as const, label: t('valuation') || 'Valuation', icon: Building2 },
    { id: 'calculator' as const, label: t('pfvCalculator'), icon: Calculator }
  ].filter((tab) => !tabsShown || tabsShown.includes(tab.id));
  // One tab is not a tab bar. When the caller names a single view, the row
  // would be a lone pill restating the heading above it.
  const showTabBar = tabs.length > 1;

  // ============================================
  // RENDER: PROPERTY VALUATION TAB
  // ============================================
  const renderValuation = () => {
    // A unit is selected → show its full valuation report instead of the list.
    if (selectedValuationUnit) {
      const unit = units.find((u: Property) => u.id === selectedValuationUnit);
      if (unit) {
        return (
          <UnitValuationReport
            unit={unit as any}
            dldUnit={getDldUnit(unit.id, unit.project)}
            lastSync={dldData?.lastSync}
            onBack={() => setSelectedValuationUnit(null)}
            benchmark={benchmark}
            benchmarkLoading={benchmarkLoading}
            timeline={timeline}
          />
        );
      }
    }

    return (
    <div>
      {units.length === 0 ? (
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '20px',
          padding: '60px 24px',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: `1px solid ${COLORS.mediumGray}`
        }}>
          <Home style={{ width: '56px', height: '56px', margin: '0 auto 20px', color: COLORS.gold, opacity: 0.6 }} />
          <h3 style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '8px', color: COLORS.textDark }}>
            {t('noPropertiesYet') || 'No Properties Yet'}
          </h3>
          <p style={{ fontSize: '0.9rem', color: COLORS.textGray }}>
            {t('pfvUploadOtpFirst')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* One tappable row per property — pick a unit to open its valuation report */}
          <p className="pr-label" style={{ margin: '0 2px 4px', color: COLORS.textGray }}>
            {t('pfvChooseProperty')}
          </p>
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: `1px solid ${COLORS.mediumGray}`
          }}>
            {units.map((unit: Property, i: number) => {
              const areaData = formatAreaDisplay(unit.area);
              const dldUnit = getDldUnit(unit.id, unit.project);
              const estimate = dldUnit?.dldMarketValue || 0;
              const conf = (dldUnit?.estimateConfidence || dldUnit?.valuationConfidence || '') as string;
              const pending = dldLoading || (dldRefreshing && !dldUnit);
              return (
                <button
                  key={unit.id}
                  onClick={() => setSelectedValuationUnit(unit.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    alignItems: 'center',
                    gap: '14px',
                    width: '100%',
                    padding: '18px 18px',
                    background: COLORS.white,
                    border: 'none',
                    borderTop: i === 0 ? 'none' : `1px solid ${COLORS.mediumGray}`,
                    textAlign: 'left',
                    cursor: 'pointer',
                    font: 'inherit',
                    minHeight: '68px'
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{
                      display: 'block', fontSize: '1.05rem', fontWeight: 700, color: COLORS.textDark
                    }}>
                      {unit.name || unit.project || t('property')}
                    </span>
                    <span style={{
                      display: 'block', fontSize: '0.9rem', color: '#3f4a5a', marginTop: '3px'
                    }}>
                      {t('unit')} {unit.unitNumber || (appLanguage() === 'de' ? 'k. A.' : 'N/A')} · {areaData.primary}
                      {unit.occupancyStatus ? ` · ${trData(unit.occupancyStatus)}` : ''}
                    </span>
                  </span>
                  {/* While the estimate is still loading, show a placeholder
                      rather than the purchase price: the purchase price is a
                      legitimate ANSWER for a unit DLD cannot value, so printing
                      it here before the payload lands makes a pending row
                      indistinguishable from a finished one — and then the
                      number changes under the reader. */}
                  <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }} aria-busy={pending}>
                    {pending ? (
                      <>
                        <span className="pr-skel" style={{ width: 92, height: 15, marginLeft: 'auto' }} />
                        <span className="pr-skel" style={{ width: 66, height: 11, marginTop: 6, marginLeft: 'auto' }} />
                      </>
                    ) : (
                      <>
                        <span style={{
                          display: 'block', fontSize: '1rem', fontWeight: 700, color: COLORS.textDark
                        }}>
                          {estimate ? compactAED(estimate) : compactAED(unit.purchaseValue || 0)}
                        </span>
                        <span style={{ display: 'block', fontSize: '0.82rem', color: '#3f4a5a', marginTop: '2px' }}>
                          {estimate
                            ? t(conf.toLowerCase() === 'high' ? 'pfvEstConfHigh'
                              : conf.toLowerCase() === 'medium' ? 'pfvEstConfMedium'
                              : 'pfvEstConfLow')
                            : t('pfvPurchasePriceNote')}
                        </span>
                      </>
                    )}
                  </span>
                  <ChevronRight style={{ width: 22, height: 22, color: COLORS.gold, flex: 'none' }} />
                </button>
              );
            })}
          </div>


          {/* Data freshness note. The manual "Sync Market Data" button was
              removed: DLD data updates automatically (UAE-side daily sync →
              server reload within minutes), and the manual endpoint is
              UAE-blocked (403) from this deployment anyway. */}
          <div style={{ textAlign: 'center', padding: '12px' }}>
            {dldData?.lastSync && (
              <p style={{ fontSize: '0.7rem', color: COLORS.textGray, marginTop: '8px' }}>
                <Shield style={{ width: 10, height: 10, display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                {t('pfvUpdatedAuto')} {new Date(dldData.lastSync).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {' '}• {t('pfvSourceDld')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="pv-root" style={{ 
      fontFamily: "'Montserrat', sans-serif",
      background: `linear-gradient(180deg, ${COLORS.gold}40 0%, ${COLORS.gold}15 120px, ${COLORS.white} 300px)`,
      minHeight: '100vh',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '8px',
      paddingBottom: '16px'
    }}>
      {/* Tab Navigation — frozen to the top while the report scrolls. The
          sticky element is the WRAPPER, not the pill: the pill is rounded and
          sits on a gradient, so content sliding under its corners showed
          through. See .pv-tabbar in index.css. */}
      {showTabBar && (
      <div className="pv-tabbar">
      <div style={{
        display: 'flex',
        gap: '6px',
        backgroundColor: COLORS.mediumGray,
        padding: '6px',
        borderRadius: '14px'
      }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                padding: '11px 4px',
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.7rem',
                whiteSpace: 'nowrap',
                fontWeight: isActive ? '700' : '500',
                backgroundColor: isActive ? COLORS.white : 'transparent',
                color: isActive ? COLORS.textDark : COLORS.textGray,
                boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.25s ease'
              }}
            >
              <Icon style={{ width: '15px', height: '15px', flex: 'none' }} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      </div>
      )}

      {/* Tab Content */}
      {activeTab === 'report' && (
        <>
          {dldLoading && <div style={{ padding: '12px 16px 0' }}><BrainLoader task="imbTaskValuation" /></div>}
          <PortfolioReport
            properties={units}
            investorName={investorName}
            investorId={investorId}
            apiUrl={apiUrl}
            dldData={dldData}
            leasesOverride={leasesOverride}
            plansOverride={plansOverride}
          />
          {/* Revenue & return used to sit in the Performance tab. That tab is
              now the Calculator, and this is real per-unit yield — so it comes
              back to the report it was taken from rather than being dropped. */}
          <div style={{ marginTop: '20px' }}>
            <PortfolioRevenue
              properties={units}
              investorId={investorId}
              dldData={dldData}
              leasesOverride={leasesOverride}
            />
          </div>
          {/* Holiday-let income from DreamHaven, for owners with an Airbnb
              apartment. The showcase (sample leases) keeps its sample report. */}
          <AirbnbReportSection units={units} demoMode={!!leasesOverride} />
        </>
      )}
      {activeTab === 'valuation' && renderValuation()}
      {activeTab === 'calculator' && (
        <PortfolioCalculator
          properties={units}
          investorId={investorId}
          investorName={investorName}
          apiUrl={apiUrl}
          dldData={dldData}
          leasesOverride={leasesOverride}
          listingsOverride={listingsOverride}
        />
      )}
    </div>
  );
};

export default PortfolioView;